import React, { useContext, useEffect, useState } from "react";
import { FlatList, Text, View, StyleSheet, Pressable } from "react-native";
import { useNavigate } from 'react-router-dom';
import { GlobalStateContext } from "../../components/general/GlobalStateProvider";
import { MaterialCommunityIcons } from "@expo/vector-icons"; // For the dot icon
import NotificationOps from "../../data/NotificationOps";
import CoachViewModal from "../../components/session/CoachViewModal";


const NotificationsPage = () => {
    const { notifications, setNotifications, padding, user } = useContext(GlobalStateContext);
    const navigate = useNavigate();

    // Modal state for CoachViewModal
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [selectedCampId, setSelectedCampId] = useState(null);
    const [modalInitialView, setModalInitialView] = useState(null);
    const [modalInitialDate, setModalInitialDate] = useState(null);
    const [targetContentId, setTargetContentId] = useState(null);
    const [targetUserId, setTargetUserId] = useState(null);

    // Count unread notifications for title display
    const unreadCount = notifications?.filter(notification => !notification.read).length || 0;

    const markAsRead = async (id) => {
        const notificationToUpdate = notifications.find((notification) => notification.id === id);
        if (notificationToUpdate && !notificationToUpdate.read && user?.userId && user?.accountType) {
            try {
                // Update in user's subcollection (works for any account type: user/host/coach)
                await NotificationOps.markAsReadForAnyAccount(user.userId, id, user.accountType);

                // Update local state optimistically
                const updatedNotifications = notifications.map((notification) =>
                    notification.id === id ? { ...notification, read: true } : notification
                );
                setNotifications(updatedNotifications);
            } catch (error) {
                console.error("Error updating notification:", error);
            }
        }
    };

    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return '';

        const now = new Date();
        const notificationTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const diffMs = now.getTime() - notificationTime.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return notificationTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const handleNotificationPress = async (notification) => {
        // Mark as read
        await markAsRead(notification.id);

        // Open session modal with appropriate view based on user type
        if (notification.sessionId && notification.campId) {
            setSelectedSessionId(notification.sessionId);
            setSelectedCampId(notification.campId);

            // For attendance notifications, open at the correct day tab
            if (notification.type === 'attendance' && notification.actionDate) {
                setModalInitialDate(notification.actionDate);
                setModalInitialView(null);
                setTargetUserId(notification.targetUserId || null);
            } else {
                setModalInitialView(null);
                setModalInitialDate(null);
                setTargetUserId(null);
            }

            setTargetContentId(null);
            setShowSessionModal(true);
        }
    };

    const handleViewComment = async (notification) => {
        // Mark as read
        await markAsRead(notification.id);

        // Open modal with specific view
        if (notification.sessionId && notification.campId) {
            const isComment = notification.type === 'coach_comment' || notification.type === 'class_comment';
            const viewType = isComment ? 'comments' : 'photos';
            setSelectedSessionId(notification.sessionId);
            setSelectedCampId(notification.campId);
            setModalInitialView(viewType);
            setModalInitialDate(notification.actionDate);

            // Set content-specific data if available
            setTargetContentId(notification.commentId || notification.photoId || null);
            setTargetUserId(notification.targetUserId || null);

            setShowSessionModal(true);
        }
    };



    const renderNotification = ({ item }) => {
        const isCoachNotification = item.type === 'coach_comment' || item.type === 'coach_photo' || item.type === 'class_comment' || item.type === 'class_photo';
        const isRegistrationNotification = item.type === 'registration';
        const isAttendanceNotification = item.type === 'attendance';
        const isNewSessionNotification = item.type === 'new_session';
        const isCoachManagementNotification = item.type === 'coach_added' || item.type === 'coach_removed';
        const isSessionAssignedNotification = item.type === 'session_assigned';
        const isSessionUnassignedNotification = item.type === 'session_unassigned';
        const notificationIcon = item.type === 'coach_comment' || item.type === 'class_comment' ? 'comment-text' :
            item.type === 'coach_photo' || item.type === 'class_photo' ? 'camera' :
                item.type === 'registration' ? 'account-check' :
                    item.type === 'attendance' ? 'clipboard-check' :
                        item.type === 'new_session' ? 'tent' :
                            item.type === 'coach_added' ? 'account-plus' :
                                item.type === 'coach_removed' ? 'account-minus' :
                                    item.type === 'session_assigned' ? 'whistle' :
                                        item.type === 'session_unassigned' ? 'whistle-outline' : 'bell';

        // Determine if current user is coach/host for this notification
        const isCoachOrHost = user?.accountType === 'host' || user?.accountType === 'coach';

        return (
            <Pressable
                onPress={() => markAsRead(item.id)}
                style={[
                    styles.notificationCard,
                    !item.read && styles.notificationUnread,
                ]}
            >
                <View style={styles.cardContent}>
                    {/* Left Section - Icon */}
                    <View style={[
                        styles.iconContainer,
                        { backgroundColor: item.read ? '#f5f5f5' : '#E8F4FD' }
                    ]}>
                        <MaterialCommunityIcons
                            name={notificationIcon}
                            size={24}
                            color={item.read ? "#999" : "#2C67D6"}
                        />
                    </View>

                    {/* Middle Section - Content */}
                    <View style={styles.contentSection}>
                        <View style={styles.titleRow}>
                            <Text style={[
                                styles.notificationTypeTitle,
                                { color: item.read ? '#666' : '#333' }
                            ]}>
                                {item.type === 'coach_comment' ? 'New Comment' :
                                    item.type === 'coach_photo' ? 'New Moment' :
                                        item.type === 'registration' ? 'New Registration' :
                                            item.type === 'attendance' ? 'Check-In' :
                                                item.type === 'new_session' ? 'New Session Published' :
                                                    item.type === 'coach_added' ? 'Added to Camp' :
                                                        item.type === 'coach_removed' ? 'Removed from Camp' :
                                                            item.type === 'session_assigned' ? 'Session Assigned' :
                                                                item.type === 'session_unassigned' ? 'Session Unassigned' : 'Notification'}
                            </Text>
                            <Text style={styles.timeAgo}>
                                {formatTimeAgo(item.createdAt)}
                            </Text>
                        </View>

                        <Text style={[
                            styles.notificationMessage,
                            { color: item.read ? '#666' : '#333' }
                        ]}>
                            {item.message}
                        </Text>

                        {/* Action Buttons */}
                        {isCoachNotification && (
                            <View style={styles.actionButtonsRow}>
                                {(() => {
                                    const isCommentType = item.type === 'coach_comment' || item.type === 'class_comment';
                                    return (
                                        <>
                                            <Pressable
                                                style={({ pressed }) => [
                                                    styles.actionButton,
                                                    pressed && styles.actionButtonPressed
                                                ]}
                                                onPress={() => handleViewComment(item)}
                                            >
                                                <MaterialCommunityIcons
                                                    name={isCommentType ? 'comment-text' : 'camera'}
                                                    size={14}
                                                    color="#2C67D6"
                                                />
                                                <Text style={styles.actionButtonText}>
                                                    View {isCommentType ? 'Comment' : 'Moment'}
                                                </Text>
                                            </Pressable>
                                            <Pressable
                                                style={({ pressed }) => [
                                                    styles.actionButton,
                                                    styles.actionButtonSecondary,
                                                    pressed && styles.actionButtonPressed
                                                ]}
                                                onPress={() => handleNotificationPress(item)}
                                            >
                                                <MaterialCommunityIcons name="eye" size={14} color="#666" />
                                                <Text style={styles.actionButtonTextSecondary}>View Session</Text>
                                            </Pressable>
                                        </>
                                    );
                                })()}
                            </View>
                        )}

                        {/* Coach Management Notification Button */}
                        {isCoachManagementNotification && item.campId && (
                            <Pressable
                                style={({ pressed }) => [
                                    styles.viewSessionButton,
                                    pressed && styles.actionButtonPressed
                                ]}
                                onPress={async () => {
                                    await markAsRead(item.id);
                                    navigate(`/camps/${item.campId}`);
                                }}
                            >
                                <MaterialCommunityIcons
                                    name="tent"
                                    size={16}
                                    color="#fff"
                                />
                                <Text style={styles.viewSessionButtonText}>
                                    View Camp
                                </Text>
                            </Pressable>
                        )}

                        {/* Session Assigned Notification Button */}
                        {isSessionAssignedNotification && item.sessionId && item.campId && (
                            <Pressable
                                style={({ pressed }) => [
                                    styles.viewSessionButton,
                                    pressed && styles.actionButtonPressed
                                ]}
                                onPress={async () => {
                                    await markAsRead(item.id);
                                    // Open the coach view modal directly on the notifications page
                                    setSelectedSessionId(item.sessionId);
                                    setSelectedCampId(item.campId);
                                    setModalInitialView(null);
                                    setModalInitialDate(null);
                                    setTargetContentId(null);
                                    setTargetUserId(null);
                                    setShowSessionModal(true);
                                }}
                            >
                                <MaterialCommunityIcons
                                    name="calendar-check"
                                    size={16}
                                    color="#fff"
                                />
                                <Text style={styles.viewSessionButtonText}>
                                    View Session
                                </Text>
                            </Pressable>
                        )}

                        {/* Session Unassigned Notification Button */}
                        {isSessionUnassignedNotification && item.campId && (
                            <Pressable
                                style={({ pressed }) => [
                                    styles.viewSessionButton,
                                    pressed && styles.actionButtonPressed
                                ]}
                                onPress={async () => {
                                    await markAsRead(item.id);
                                    navigate(`/camps/${item.campId}`);
                                }}
                            >
                                <MaterialCommunityIcons
                                    name="tent"
                                    size={16}
                                    color="#fff"
                                />
                                <Text style={styles.viewSessionButtonText}>
                                    View Camp
                                </Text>
                            </Pressable>
                        )}

                        {/* Registration, Attendance, or New Session Notification Button */}
                        {(isRegistrationNotification || isAttendanceNotification || isNewSessionNotification) && item.campId && (
                            <Pressable
                                style={({ pressed }) => [
                                    styles.viewSessionButton,
                                    pressed && styles.actionButtonPressed
                                ]}
                                onPress={async () => {
                                    await markAsRead(item.id);
                                    if (isNewSessionNotification) {
                                        // Navigate to camp page for new session notifications
                                        navigate(`/camps/${item.campId}`);
                                    } else {
                                        // Open session modal for registration/attendance notifications
                                        handleNotificationPress(item);
                                    }
                                }}
                            >
                                <MaterialCommunityIcons
                                    name={isNewSessionNotification ? 'tent' :
                                        isAttendanceNotification ? 'calendar-check' : 'calendar-clock'}
                                    size={16}
                                    color="#fff"
                                />
                                <Text style={styles.viewSessionButtonText}>
                                    {isNewSessionNotification ? 'View Details' :
                                        isCoachOrHost ? 'View Session' : 'View Details'}
                                </Text>
                            </Pressable>
                        )}
                    </View>

                    {/* Right Section - Unread Indicator */}
                    {!item.read && <View style={styles.unreadIndicator} />}
                </View>
            </Pressable>
        );
    };

    // Determine if user is coach/host for modal view
    const isCoachOrHost = user?.accountType === 'host' || user?.accountType === 'coach';

    return (
        <View style={[styles.container, { paddingHorizontal: padding }]}>
            <View style={styles.titleContainer}>
                <Text style={styles.title}>Notifications</Text>
                {unreadCount > 0 && (
                    <View style={styles.titleBadge}>
                        <Text style={styles.titleBadgeText}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </Text>
                    </View>
                )}
            </View>

            {/* Empty State */}
            {!notifications || notifications.length === 0 ? (
                <View style={styles.emptyStateContainer}>
                    <View style={styles.emptyStateIconContainer}>
                        <MaterialCommunityIcons name="bell-outline" size={80} color="#E0E0E0" />
                    </View>
                    <Text style={styles.emptyStateTitle}>No Notifications Yet</Text>
                    <Text style={styles.emptyStateText}>
                        {isCoachOrHost
                            ? "You'll be notified when families register for your sessions, or when there are important updates."
                            : "You'll be notified when there are updates about your registrations, new sessions from your favorite camps, comments from coaches, and more."}
                    </Text>
                </View>
            ) : (
                <FlatList
                    data={notifications}
                    keyExtractor={(item) => item.id}
                    renderItem={renderNotification}
                    contentContainerStyle={styles.listContent}
                />
            )}

            {/* Session Modal */}
            {
                showSessionModal && <CoachViewModal
                    visible={showSessionModal}
                    onClose={() => {
                        setShowSessionModal(false);
                        setModalInitialDate(null);
                        setModalInitialView(null);
                        setTargetContentId(null);
                        setTargetUserId(null);
                    }}
                    sessionId={selectedSessionId}
                    campId={selectedCampId}
                    currentUser={user}
                    isCoach={isCoachOrHost} // Show coach view for coaches/hosts
                    initialView={modalInitialView}
                    initialDate={modalInitialDate}
                    targetContentId={targetContentId}
                    targetUserId={targetUserId}
                />}
        </View>
    );
};

export default NotificationsPage;

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 20,
        marginBottom: 50,
    },
    title: {
        fontSize: 40,
        fontWeight: 'bold',
        marginRight: 15,
    },
    titleBadge: {
        backgroundColor: '#FF4444',
        borderRadius: 15,
        minWidth: 30,
        height: 30,
        justifyContent: 'center',
        alignItems: 'center',
    },
    titleBadgeText: {
        color: 'white',
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    listContent: {
        paddingBottom: 20,
    },
    notificationCard: {
        backgroundColor: '#fff',
        borderRadius: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.1,
        shadowRadius: 6,
        elevation: 4,
        overflow: 'hidden',
    },
    notificationUnread: {
        borderLeftWidth: 4,
        borderLeftColor: '#2C67D6',
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 6,
    },
    cardContent: {
        flexDirection: 'row',
        padding: 20,
        alignItems: 'flex-start',
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 16,
    },
    contentSection: {
        flex: 1,
        paddingRight: 12,
    },
    titleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    notificationTypeTitle: {
        fontSize: 16,
        fontWeight: '700',
        flex: 1,
    },
    timeAgo: {
        fontSize: 12,
        color: '#999',
        fontWeight: '500',
    },
    actionButtonsRow: {
        flexDirection: 'row',
        marginTop: 12,
        gap: 8,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: '#e9ecef',
        flex: 1,
        justifyContent: 'center',
    },
    actionButtonSecondary: {
        backgroundColor: '#fff',
        borderColor: '#ddd',
    },
    actionButtonPressed: {
        backgroundColor: '#e9ecef',
        transform: [{ scale: 0.98 }],
    },
    actionButtonText: {
        color: '#2C67D6',
        fontSize: 13,
        fontWeight: '600',
        marginLeft: 6,
    },
    actionButtonTextSecondary: {
        color: '#666',
        fontSize: 13,
        fontWeight: '600',
        marginLeft: 6,
    },
    unreadIndicator: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: '#2C67D6',
        marginTop: 4,
    },
    notificationMessage: {
        fontSize: 15,
        color: "#333",
        lineHeight: 20,
        marginBottom: 4,
    },
    notificationTimestamp: {
        fontSize: 12,
        color: "#777",
    },
    viewSessionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#3C8248',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        marginTop: 12,
        gap: 8,
    },
    viewSessionButtonText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '600',
    },
    emptyStateContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
        paddingVertical: 60,
        minHeight: 500,
    },
    emptyStateIconContainer: {
        width: 140,
        height: 140,
        borderRadius: 70,
        backgroundColor: '#F8F9FA',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 24,
    },
    emptyStateTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#333',
        marginBottom: 12,
        textAlign: 'center',
    },
    emptyStateText: {
        fontSize: 16,
        color: '#666',
        lineHeight: 24,
        textAlign: 'center',
        maxWidth: 500,
    },
});

